import React, { useState, useEffect } from 'react';
import { instancesApi } from '../../services/api';

function RatingBar({ value, max = 5 }) {
  if (value === null || value === undefined) return <span style={{color: '#999'}}>—</span>;
  const pct = (parseFloat(value) / max) * 100;
  const color = pct >= 80 ? '#22c55e' : pct >= 60 ? '#eab308' : '#ef4444';
  return (
    <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
      <div style={{flex: 1, height: '8px', background: '#e5e7eb', borderRadius: '4px', minWidth: '60px'}}>
        <div style={{width: `${pct}%`, height: '100%', background: color, borderRadius: '4px', transition: 'width 0.3s'}} />
      </div>
      <span style={{fontWeight: 600, minWidth: '32px'}}>{value}/{max}</span>
    </div>
  );
}

function NpsDisplay({ nps }) {
  if (!nps || nps.total === 0) return <p style={{color: '#999'}}>Inga NPS-svar ännu</p>;
  const color = nps.score >= 50 ? '#22c55e' : nps.score >= 0 ? '#eab308' : '#ef4444';
  return (
    <div style={{textAlign: 'center', padding: '15px'}}>
      <div style={{fontSize: '48px', fontWeight: 700, color}}>{nps.score}</div>
      <div style={{fontSize: '14px', color: '#666', marginBottom: '12px'}}>NPS-poäng</div>
      <div style={{display: 'flex', justifyContent: 'center', gap: '20px', fontSize: '13px'}}>
        <span style={{color: '#22c55e'}}>Promoters: {nps.promoters}</span>
        <span style={{color: '#eab308'}}>Passiva: {nps.passives}</span>
        <span style={{color: '#ef4444'}}>Kritiker: {nps.detractors}</span>
      </div>
    </div>
  );
}

function SurveyResultsModal({ show, instance, onClose }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (show && instance) {
      setLoading(true);
      setError('');
      instancesApi.getSurveyStats(instance.id)
        .then(res => setStats(res.data.data))
        .catch(err => setError('Kunde inte hämta enkätresultat'))
        .finally(() => setLoading(false));
    }
  }, [show, instance]);

  if (!show) return null;

  const courseName = instance?.course?.name || instance?.courseName || 'Kurs';
  const courseDate = instance?.startDate ? new Date(instance.startDate).toLocaleDateString('sv-SE') : '';

  const handleExport = () => {
    if (!stats || stats.responseCount === 0) return;
    const w = window.open('', '_blank');
    w.document.write(generateExportHtml(courseName, courseDate, instance, stats));
    w.document.close();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={e => e.stopPropagation()} style={{maxHeight: '85vh', overflow: 'auto'}}>
        <h2>Enkätresultat: {courseName}</h2>
        <p style={{color: '#666', marginTop: '-8px'}}>{courseDate} — {instance?.location || instance?.venue?.name || ''}</p>

        {loading && <p>Laddar...</p>}
        {error && <div className="alert alert-danger">{error}</div>}

        {stats && stats.responseCount === 0 && (
          <div className="no-data"><p>Inga enkätsvar ännu för detta kurstillfälle.</p></div>
        )}

        {stats && stats.responseCount > 0 && (
          <>
            <p style={{fontWeight: 600, marginBottom: '20px'}}>
              {stats.responseCount} svar inkomna
            </p>

            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px'}}>
              <div style={{background: '#f8fafc', padding: '15px', borderRadius: '8px'}}>
                <h4 style={{margin: '0 0 12px', color: '#0c4da2'}}>Utbildare</h4>
                <table style={{width: '100%', fontSize: '14px'}}>
                  <tbody>
                    <tr><td style={{padding: '4px 0'}}>Engagemang</td><td><RatingBar value={stats.averages.trainerEngagement} /></td></tr>
                    <tr><td style={{padding: '4px 0'}}>Pedagogik</td><td><RatingBar value={stats.averages.trainerTeaching} /></td></tr>
                    <tr><td style={{padding: '4px 0'}}>Kunskap</td><td><RatingBar value={stats.averages.trainerKnowledge} /></td></tr>
                  </tbody>
                </table>
              </div>
              <div style={{background: '#f8fafc', padding: '15px', borderRadius: '8px'}}>
                <h4 style={{margin: '0 0 12px', color: '#0c4da2'}}>Kurs</h4>
                <table style={{width: '100%', fontSize: '14px'}}>
                  <tbody>
                    <tr><td style={{padding: '4px 0'}}>Innehåll</td><td><RatingBar value={stats.averages.courseContent} /></td></tr>
                    <tr><td style={{padding: '4px 0'}}>Användning</td><td><RatingBar value={stats.averages.usageRating} /></td></tr>
                    <tr><td style={{padding: '4px 0'}}>Nöjdhet</td><td><RatingBar value={stats.averages.satisfactionRating} /></td></tr>
                    <tr><td style={{padding: '4px 0'}}>Lunch</td><td><RatingBar value={stats.averages.courseLunch} /></td></tr>
                    <tr><td style={{padding: '4px 0'}}>Fika</td><td><RatingBar value={stats.averages.courseFika} /></td></tr>
                    <tr><td style={{padding: '4px 0'}}>Lokal</td><td><RatingBar value={stats.averages.courseVenue} /></td></tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div style={{background: '#f8fafc', padding: '15px', borderRadius: '8px', marginBottom: '20px'}}>
              <h4 style={{margin: '0 0 8px', color: '#0c4da2'}}>Net Promoter Score</h4>
              <NpsDisplay nps={stats.nps} />
            </div>

            {(stats.freeText.likedMost.length > 0 || stats.freeText.improvements.length > 0 || stats.freeText.otherComments.length > 0) && (
              <div style={{marginBottom: '20px'}}>
                <h4 style={{color: '#0c4da2'}}>Fritextsvar</h4>
                {stats.freeText.likedMost.length > 0 && (
                  <div style={{marginBottom: '12px'}}>
                    <strong>Vad var bäst?</strong>
                    <ul style={{margin: '4px 0', paddingLeft: '20px'}}>
                      {stats.freeText.likedMost.map((t, i) => <li key={i} style={{marginBottom: '4px'}}>{t}</li>)}
                    </ul>
                  </div>
                )}
                {stats.freeText.improvements.length > 0 && (
                  <div style={{marginBottom: '12px'}}>
                    <strong>Förbättringsförslag</strong>
                    <ul style={{margin: '4px 0', paddingLeft: '20px'}}>
                      {stats.freeText.improvements.map((t, i) => <li key={i} style={{marginBottom: '4px'}}>{t}</li>)}
                    </ul>
                  </div>
                )}
                {stats.freeText.otherComments.length > 0 && (
                  <div style={{marginBottom: '12px'}}>
                    <strong>Övriga kommentarer</strong>
                    <ul style={{margin: '4px 0', paddingLeft: '20px'}}>
                      {stats.freeText.otherComments.map((t, i) => <li key={i} style={{marginBottom: '4px'}}>{t}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {(stats.canReference.yes > 0 || stats.canReference.contactFirst > 0) && (
              <div style={{background: '#f0fdf4', padding: '12px', borderRadius: '8px', marginBottom: '20px', fontSize: '14px'}}>
                <strong>Referensvilliga:</strong> {stats.canReference.yes} st (ja) + {stats.canReference.contactFirst} st (kontakta först)
              </div>
            )}
          </>
        )}

        <div className="form-actions">
          {stats && stats.responseCount > 0 && (
            <button type="button" className="btn btn-primary" onClick={handleExport}>
              Exportera resultat
            </button>
          )}
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Stäng
          </button>
        </div>
      </div>
    </div>
  );
}

function generateExportHtml(courseName, courseDate, instance, stats) {
  const ratingRow = (label, value) => {
    if (value === null || value === undefined) return '';
    return `<tr><td style="padding:6px 12px">${label}</td><td style="padding:6px 12px; font-weight:600">${value}/5</td></tr>`;
  };

  const freeTextSection = (title, items) => {
    if (!items || items.length === 0) return '';
    return `
      <h3 style="color:#0c4da2; margin-top:24px">${title}</h3>
      <ul>${items.map(t => `<li style="margin-bottom:6px">${t.replace(/</g, '&lt;')}</li>`).join('')}</ul>
    `;
  };

  const npsColor = stats.nps.score >= 50 ? '#22c55e' : stats.nps.score >= 0 ? '#eab308' : '#ef4444';

  return `<!DOCTYPE html>
<html lang="sv">
<head>
  <meta charset="UTF-8">
  <title>Enkätresultat - ${courseName}</title>
  <style>
    @media print { body { margin: 0; } .no-print { display: none; } }
    body { font-family: 'Segoe UI', Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 40px; color: #333; }
    h1 { color: #0c4da2; margin-bottom: 4px; }
    .subtitle { color: #666; margin-bottom: 24px; }
    table { border-collapse: collapse; width: 100%; margin-bottom: 16px; }
    th, td { text-align: left; padding: 8px 12px; border-bottom: 1px solid #e5e7eb; }
    th { background: #f8fafc; color: #0c4da2; }
    .nps-box { text-align: center; padding: 20px; background: #f8fafc; border-radius: 8px; margin: 20px 0; }
    .nps-score { font-size: 48px; font-weight: 700; }
    .section { margin-top: 30px; }
    .print-btn { padding: 10px 20px; background: #0c4da2; color: white; border: none; border-radius: 5px; cursor: pointer; font-size: 14px; }
  </style>
</head>
<body>
  <div class="no-print" style="margin-bottom:20px">
    <button class="print-btn" onclick="window.print()">Skriv ut / Spara som PDF</button>
  </div>
  <h1>Enkätresultat</h1>
  <p class="subtitle">${courseName} — ${courseDate}${instance?.location ? ` — ${instance.location}` : ''}${instance?.instructorName ? ` — Utbildare: ${instance.instructorName}` : ''}</p>
  <p><strong>${stats.responseCount} svar</strong></p>

  <div class="section">
    <h2 style="color:#0c4da2">Betyg – Utbildare</h2>
    <table>
      <thead><tr><th>Fråga</th><th>Snitt (1–5)</th></tr></thead>
      <tbody>
        ${ratingRow('Engagemang', stats.averages.trainerEngagement)}
        ${ratingRow('Pedagogik', stats.averages.trainerTeaching)}
        ${ratingRow('Kunskap', stats.averages.trainerKnowledge)}
      </tbody>
    </table>
  </div>

  <div class="section">
    <h2 style="color:#0c4da2">Betyg – Kurs</h2>
    <table>
      <thead><tr><th>Fråga</th><th>Snitt (1–5)</th></tr></thead>
      <tbody>
        ${ratingRow('Innehåll', stats.averages.courseContent)}
        ${ratingRow('Förväntad användning', stats.averages.usageRating)}
        ${ratingRow('Nöjdhet', stats.averages.satisfactionRating)}
        ${ratingRow('Lunch', stats.averages.courseLunch)}
        ${ratingRow('Fika', stats.averages.courseFika)}
        ${ratingRow('Lokal', stats.averages.courseVenue)}
      </tbody>
    </table>
  </div>

  <div class="nps-box">
    <div class="nps-score" style="color:${npsColor}">${stats.nps.score}</div>
    <div style="color:#666">Net Promoter Score</div>
    <div style="margin-top:8px; font-size:13px">
      <span style="color:#22c55e">Promoters: ${stats.nps.promoters}</span> &nbsp;
      <span style="color:#eab308">Passiva: ${stats.nps.passives}</span> &nbsp;
      <span style="color:#ef4444">Kritiker: ${stats.nps.detractors}</span>
    </div>
  </div>

  ${freeTextSection('Vad var bäst?', stats.freeText.likedMost)}
  ${freeTextSection('Förbättringsförslag', stats.freeText.improvements)}
  ${freeTextSection('Övriga kommentarer', stats.freeText.otherComments)}

  <p style="color:#999; margin-top:40px; font-size:12px">Genererat från KursAdmin — Projektledarpodden</p>
</body>
</html>`;
}

export default SurveyResultsModal;
